'use client'

import { Select } from '@/components/ui/select'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, formatPrice } from '@/lib/utils'
import { CampaignProduct } from '@/types/campaign'
import { Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useMemo, useState } from 'react'

export interface SelectedProductVariantRow {
  readonly rowId: string
  readonly color: string
  readonly size: string
  readonly variantId: string
}

interface CampaignProductItemProps {
  product: CampaignProduct
  // Có thể chọn nhiều tổ hợp màu/size khác nhau của cùng 1 sản phẩm (mỗi tổ
  // hợp số lượng cố định là 1) - ví dụ Quần Đen-XL và Quần Đen-L là 2 dòng
  // riêng biệt.
  selectedRows: readonly SelectedProductVariantRow[]
  onAddVariant: (product: CampaignProduct, color: string, size: string) => void
}

const PILL_TRIGGER_CLASS =
  'h-8 rounded-full border-none bg-[#f1f1f1] px-3 text-xs text-neutral-900 hover:bg-[#e5e5e5] md:text-sm'

// Thứ tự chuẩn cho size chữ: S -> M -> L -> XL -> 2XL -> 3XL ...
const LETTER_SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL']

const getSizeSortRank = (rawSize: string): number => {
  const size = rawSize.trim().toUpperCase()
  const letterIndex = LETTER_SIZE_ORDER.indexOf(size)
  if (letterIndex !== -1) return letterIndex

  // Dạng 2XL/3XL/... xếp tiếp ngay sau XL theo bội số.
  const multiXlMatch = size.match(/^(\d+)XL$/)
  if (multiXlMatch) {
    return LETTER_SIZE_ORDER.length - 1 + Number(multiXlMatch[1])
  }

  return NaN
}

const compareSizeAsc = (a: string, b: string): number => {
  const rankA = getSizeSortRank(a)
  const rankB = getSizeSortRank(b)
  // Cả 2 đều là size chữ (S/M/L/XL/2XL...) -> so theo thứ tự chuẩn.
  if (!Number.isNaN(rankA) && !Number.isNaN(rankB)) return rankA - rankB
  // Còn lại (size số như 28/29/30, hoặc không xác định) -> so tự nhiên.
  return a.localeCompare(b, undefined, { numeric: true })
}

export const CampaignProductItem = ({
  product,
  selectedRows,
  onAddVariant,
}: CampaignProductItemProps) => {
  const t = useTranslations('community_threads.campaign.product_item')
  const formattedPrice = formatPrice(product.price)

  const [draftColor, setDraftColor] = useState('')
  const [draftSize, setDraftSize] = useState('')

  const activeColorData = product.colors.find((c) => c.name === draftColor)
  const thumbnailImage =
    activeColorData?.thumbnail || activeColorData?.image || product.image

  // Size hiển thị phải giới hạn theo màu đang chọn - nếu không, người dùng
  // có thể chọn một cặp màu/size không tồn tại variant thật nào.
  const sizesForColor = (color: string) => {
    if (!product.variants?.length) return product.sizes
    const sizes = new Set(
      product.variants.filter((v) => v.color === color).map((v) => v.size)
    )
    return product.sizes.filter((size) => sizes.has(size))
  }

  // Loại bỏ size đã thêm cho đúng màu đang chọn khỏi danh sách - tránh để
  // người dùng chọn phải tổ hợp trùng rồi mới thấy báo lỗi.
  const availableSizes = useMemo(() => {
    const sortBySizeAsc = (sizes: readonly string[]) =>
      [...sizes].sort(compareSizeAsc)
    const sizes = draftColor ? sizesForColor(draftColor) : product.sizes
    if (!draftColor) return sortBySizeAsc(sizes)
    const addedSizes = new Set(
      selectedRows
        .filter((row) => row.color === draftColor)
        .map((row) => row.size)
    )
    return sortBySizeAsc(sizes.filter((size) => !addedSizes.has(size)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sizesForColor is pure derived from product, không cần liệt kê
  }, [draftColor, product.sizes, product.variants, selectedRows])

  const handleChangeColor = (color: string) => {
    setDraftColor(color)
    if (draftSize && !sizesForColor(color).includes(draftSize)) {
      setDraftSize('')
    }
  }

  const handleChangeSize = (size: string) => {
    setDraftSize(size)
  }

  const matchedVariant = (product.variants ?? []).find(
    (v) => v.color === draftColor && v.size === draftSize
  )
  const isDuplicate = selectedRows.some(
    (row) => row.color === draftColor && row.size === draftSize
  )
  const canAdd = !!draftColor && !!draftSize && !!matchedVariant && !isDuplicate

  const handleAdd = () => {
    if (!canAdd) return
    onAddVariant(product, draftColor, draftSize)
    // Chỉ xoá size, giữ lại màu - luồng thường gặp là chọn 1 màu rồi thêm
    // lần lượt nhiều size khác nhau (vd: Đen-XL, Đen-L), nên không bắt
    // người dùng chọn lại màu sau mỗi lần thêm.
    setDraftSize('')
  }

  return (
    <div className='flex h-full overflow-hidden rounded-lg bg-neutral-50'>
      <div className='relative aspect-[107/143] w-[107px] shrink-0 overflow-hidden bg-gray-100 md:aspect-[136/180] md:w-[136px]'>
        {product.badge && (
          <span className='absolute left-2 top-2 z-10 rounded-full bg-[#D9FF00] px-2 py-0.5 text-[10px] font-bold text-black'>
            {product.badge}
          </span>
        )}
        <Image
          src={ImageHelper.getN7ImageUrl(thumbnailImage)}
          alt={product.name}
          width={300}
          height={300}
          className='h-full w-full object-cover'
        />
      </div>

      <div className='flex min-w-0 flex-1 flex-col justify-between gap-3 p-3'>
        <div className='space-y-2'>
          <div>
            <p className='line-clamp-2 text-xs text-neutral-900 md:text-sm'>
              {product.name}
            </p>
          </div>

          <div className='flex flex-wrap items-start gap-2'>
            {product.colors.length > 0 && (
              <Select
                placeholder={t('color_placeholder')}
                value={draftColor}
                onValueChange={handleChangeColor}
                options={product.colors.map((color) => ({
                  label: color.name,
                  value: color.name,
                }))}
                className={{ trigger: PILL_TRIGGER_CLASS }}
              />
            )}
            {availableSizes.length > 0 && (
              <Select
                placeholder={t('size_placeholder')}
                value={draftSize}
                onValueChange={handleChangeSize}
                options={availableSizes.map((size) => ({
                  label: size,
                  value: size,
                }))}
                className={{ trigger: PILL_TRIGGER_CLASS }}
              />
            )}
          </div>

          <p className='text-xs font-bold text-neutral-900 md:text-sm'>
            {formattedPrice}
          </p>

          {draftColor && draftSize && isDuplicate && (
            <p className='text-xs text-error-500'>
              {t('duplicate_variant_error')}
            </p>
          )}
        </div>

        <div className='relative'>
          <button
            type='button'
            onClick={handleAdd}
            disabled={!canAdd}
            className={cn(
              'flex h-10 w-full items-center justify-center gap-1.5 rounded-full border text-xs font-semibold uppercase transition-colors md:text-sm',
              canAdd
                ? 'border-neutral-900 bg-transparent text-neutral-800 hover:bg-neutral-100'
                : 'cursor-not-allowed border-neutral-300 bg-transparent text-neutral-400'
            )}
          >
            {t('add_variant')} <Plus size={16} />
          </button>
          {selectedRows.length > 0 && (
            <span className='absolute -top-2 right-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-white'>
              {selectedRows.length}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
